<template>
  <div class="application-status-sync-detail-container" v-rect-loading="loading">
    <NoBorderTable
      class="table"
      :noOperation="true"
      :tableColumns="tableColumns"
      :listData="displayList"
      :rowClassName="tableRowClassName"
    >
      <template #btn-group>
        <el-button v-rect-loading="exportStatus.loading" class="blue-btn export-btn" @click="handleExport">{{ $t(`${i18nPrefix}.btn.export`) }}</el-button>
      </template>
    </NoBorderTable>
    <el-pagination
      class="pagination"
      background
      layout="total, sizes, prev, pager, next"
      :total="pageInfo.total"
      :page-count="pageInfo.page"
      :page-size="pageInfo.pageSize"
      @current-change='handlePageChange'
      @size-change='handleSizeChange'
      >
    </el-pagination>
  </div>
</template>

<script>
import NoBorderTable from '@/components/tableList/noBorderTable.vue'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'
import _ from 'lodash'
import { AsyncTask } from '@/utils/common'

export default {
  components: {
    NoBorderTable
  },
  props: {
    historyId: {
      type: Number,
      required: true
    },
    rootCompanyId: {
      required: true
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.apScheduler',
      loading: false,
      dataList: [],
      displayList: [],
      pageInfo: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      exportStatus: {
        loading: false
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.dialog.jobId`),
          dataKey: 'job_id'
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.jobName`),
          dataKey: 'i18nTitle'
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.atsJobId`),
          dataKey: 'ats_job_id'
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.candidateId`),
          dataKey: 'candidate_id'
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.candidateName`),
          dataKey: 'candidate_name'
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.candidateEmail`),
          dataKey: 'candidate_email'
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.fromCandidateStatus`),
          dataKey: 'from_status.i18nName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.toCandidateStatus`),
          dataKey: 'to_status.i18nName'
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.syncStatus`),
          dataKey: 'sync_status',
          formatter: (data, row) => {
            if (!row.is_deleted) {
              return this.$t(`${this.i18nPrefix}.recordStatus.${data}`)
            }
            return ''
          }
        },
        {
          label: this.$t(`${this.i18nPrefix}.dialog.syncErrorType`),
          dataKey: 'sync_error_type',
          formatter: (data, row) => {
            if (!data) {
              return
            }
            return this.$t(`${this.i18nPrefix}.ERROR_TYPE.${data}`)
          }
        }
      ]
      return columns
    }
  },
  async created () {
    this.initDataList()
  },
  methods: {
    ...mapActions('apScheduler', ['getApplicationStatusSyncDetailInfoByHistoryId', 'createExportApplicationStatusSyncRecordTask', 'getExportApplicationStatusSyncRecordTaskInfo']),
    async initDataList () {
      this.loading = true
      try {
        const resp = await this.getApplicationStatusSyncDetailInfoByHistoryId(this.historyId)
        if (resp) {
          this.dataList = _.map(resp, (data) => {
            addI18nProperty(data, 'i18nTitle', 'cn_title', 'title')
            addI18nProperty(data.from_status, 'i18nName', 'name_cn', 'name_en')
            addI18nProperty(data.to_status, 'i18nName', 'name_cn', 'name_en')
            return data
          })
          this.handlePageChange(1)
        }
      } finally {
        this.loading = false
      }
    },
    tableRowClassName ({ row, rowIndex }) {
      if (rowIndex % 2 === 0) {
        return 'gray-background'
      }
    },
    handlePageChange (val) {
      this.loading = true
      const dataList = this.dataList
      try {
        this.pageInfo.page = val
        const startIndex = (this.pageInfo.page - 1) * this.pageInfo.pageSize
        if (dataList) {
          this.pageInfo.total = dataList.length
          this.displayList = dataList.slice(startIndex, startIndex + this.pageInfo.pageSize)
        }
      } finally {
        setTimeout(() => {
          this.loading = false
        }, 200)
      }
    },
    handleSizeChange (val) {
      this.pageInfo.pageSize = val
      this.handlePageChange(1)
    },
    handleExport () {
      this.exportStatus.loading = true
      const exportOfferRecordStatus = {
        finished: false,
        task: null
      }
      exportOfferRecordStatus.finished = false
      exportOfferRecordStatus.task = new AsyncTask(() => this.createExportApplicationStatusSyncRecordTask({
        root_company_id: this.rootCompanyId,
        history_id: this.historyId
      }), (taskId) => this.getExportApplicationStatusSyncRecordTaskInfo({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/v-admin/integration/application-status-sync-export/?task_id=${taskId}&download=1`)
        exportOfferRecordStatus.finished = false
        exportOfferRecordStatus.task = null
        this.exportStatus.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.application-status-sync-detail-container {
  ::v-deep .el-table {
    border: 0px;

    .el-table__header {
      .el-table__cell {
        font-size: 14px;
        padding: 20px 0;
        color: #000000;
        font-weight: 600;

        &.is-leaf {
          background-color: #FFFFFF;
        }
      }
    }

    .el-table__body {
        .el-table__cell {
          padding: 20px 0;

          .cell {
            padding: 0 5px;
          }
        }

        .gray-background {
          background: #f7f7f7;
        }
    }
  }

  .export-btn {
    border-radius: 4px;
    ::v-deep {
      .el-loading-mask {
        .el-loading-spinner {
          margin-top: -14px;

          .loading-spinner {
            .spinner {
              height: 28px;
            }
          }
        }
      }
    }
  }

  .pagination {
    text-align: right;
    margin-right: 10px;
    padding: 10px;
  }
}
</style>
